<!--
  ThreadColorPicker.vue -- choose an orderable Madeira thread, three ways in.

  Card: Embroidery colorway editing (MUST-SHIP) https://trello.com/c/2hcgDM7J

  A decorator arrives at "pick a thread" with different information -- a code
  off a spool, a brand's Pantone number, or just "something warm red" -- so
  this offers THREE entries into ONE mechanism (one grid, one network of
  calls) rather than three separate UIs:

    1. free text (Madeira code / Pantone code / family name -- one box, hits
       GET /wilcom/colors/search)
    2. browse-by-family (11 swatch buttons from GET /wilcom/colors/families,
       filters the same grid)
    3. direct hex entry (POST /wilcom/colors/resolve, winner="hex")

  Ported from the interaction model in Mike's tf-tools/wilcom-true-preview
  prototype (client/src/App.jsx / ColorPicker.jsx) with his permission (Jake,
  2026-08-19) -- rebuilt here in Vue 3 + Vuetify 3, not copied.

  THIS COMPONENT ONLY EMITS. It reads three GET/POST-idempotent endpoints and
  never writes -- the parent (e.g. ColorwayEditor's per-stop edit state) owns
  what a selection means. See docs/design-philosophy.md #6: one write model
  per surface, and this surface has none.

  APPROXIMATION IS VISIBLE, matching ColorwayEditor's existing affordance
  (`cwe__approx`, the "closest match" pill): a hex resolve that is not exact
  shows the same "~" mark rather than looking like a confirmed catalogue
  entry. An unknown code says so and shows NO color -- never a guess, for the
  same reason ColorwayEditor never invents one: an unfulfillable code must not
  reach an order line looking fulfillable.
-->
<template>
    <div class="tcp" data-testid="thread-color-picker">
        <!-- Way in #1: free text, one box, three vocabularies. -->
        <div class="tcp__search">
            <v-icon aria-hidden="true" class="tcp__search-icon">search</v-icon>
            <input
                class="tcp__search-input"
                type="text"
                placeholder="Search a code, Pantone, or color (e.g. 1842, 2101 C, purple)"
                data-testid="thread-picker-search-input"
                aria-label="Search threads by code, Pantone, or color"
                :value="query"
                @input="onQueryInput($event.target.value)"
            />
        </div>

        <!-- Way in #2: browse by family. Filters the SAME grid as the search
             box -- clicking a family is equivalent to typing its name, so
             there is exactly one result-fetching mechanism underneath. -->
        <ul class="tcp__families" data-testid="thread-picker-families">
            <li v-for="fam in families" :key="fam.name">
                <button
                    type="button"
                    class="tcp__family"
                    :class="{ 'tcp__family--on': fam.name === activeFamily }"
                    :data-testid="`thread-picker-family-${fam.name}`"
                    :aria-pressed="fam.name === activeFamily"
                    :title="fam.name"
                    @click="toggleFamily(fam.name)"
                >
                    <span
                        class="tcp__family-swatch"
                        :style="{ background: fam.swatch }"
                        aria-hidden="true"
                    />
                    <span class="tcp__family-name">{{ fam.name }}</span>
                </button>
            </li>
        </ul>

        <!-- Way in #3: direct hex entry, resolved via the SAME live-link the
             editor uses for its per-stop hex field (winner="hex"). -->
        <div class="tcp__hex">
            <label class="tcp__hex-label" for="tcp-hex-input">or enter a hex</label>
            <input
                id="tcp-hex-input"
                class="tcp__hex-input"
                type="text"
                placeholder="#RRGGBB"
                data-testid="thread-picker-hex-input"
                :value="hexQuery"
                @input="onHexInput($event.target.value)"
            />
            <div v-if="hexResult" class="tcp__hex-answer">
                <button
                    type="button"
                    class="tcp__hex-result"
                    data-testid="thread-picker-hex-result"
                    @click="selectThread(hexResult)"
                >
                    <span
                        class="tcp__swatch"
                        :style="{ background: hexResult.hex || '#ccc' }"
                        aria-hidden="true"
                    />
                    <span>
                        <span
                            v-if="!hexResult.exact"
                            class="tcp__approx-mark"
                            data-testid="thread-picker-hex-approx"
                            aria-hidden="true"
                            >~</span
                        >
                        {{ hexResult.code }} -- {{ hexResult.brand }}
                    </span>
                </button>
            </div>
            <p
                v-else-if="hexUnknown"
                class="tcp__hex-unknown"
                data-testid="thread-picker-hex-unknown"
            >
                Not in the {{ chart }} book -- no thread matches that code.
            </p>
        </div>

        <!-- The one result grid, fed by search OR family (or both). -->
        <p
            v-if="loading"
            class="tcp__loading"
            data-testid="thread-picker-loading"
        >
            Searching...
        </p>
        <p
            v-else-if="!results.length"
            class="tcp__empty"
            data-testid="thread-picker-empty"
        >
            {{ emptyMessage }}
        </p>
        <ul v-else class="tcp__grid" data-testid="thread-picker-grid">
            <li v-for="thread in results" :key="thread.code">
                <button
                    type="button"
                    class="tcp__result"
                    :data-testid="`thread-picker-result-${thread.code}`"
                    @click="selectThread(thread)"
                >
                    <span
                        class="tcp__swatch"
                        :style="{ background: thread.hex || '#ccc' }"
                        aria-hidden="true"
                    />
                    <span class="tcp__result-body">
                        <span class="tcp__result-code">{{ thread.code }}</span>
                        <span class="tcp__result-brand">{{ thread.brand }}</span>
                        <span class="tcp__result-family">{{ thread.family }}</span>
                        <span v-if="thread.pantone" class="tcp__result-pantone">
                            PMS {{ thread.pantone }}
                        </span>
                    </span>
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
// The SHARED api client -- NOT this.$axios. See ColorwayEditor.vue's comment:
// a relative /api/... on $axios hits the FRONTEND host and silently returns
// the SPA's index.html instead of erroring. Verified on 8083.
import api from '@/services/api'

// Matches ColorwayEditor's per-stop hex<->Madeira debounce (180ms, see
// ColorwayEditor.vue's RESOLVE_DEBOUNCE_MS) -- short enough to feel live
// while typing, long enough that a full code is one request, not one per
// keystroke. Applied to the free-text search box too, so all three ways in
// share one typing rhythm.
const SEARCH_DEBOUNCE_MS = 180
const RESOLVE_DEBOUNCE_MS = 180

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export default {
    name: 'ThreadColorPicker',

    props: {
        chart: { type: String, default: 'polyneon' },
    },

    emits: ['select'],

    data() {
        return {
            families: [],
            query: '',
            activeFamily: '',
            results: [],
            loading: false,
            searched: false,
            hexQuery: '',
            hexResult: null,
            hexUnknown: false,
            _searchTimer: null,
            _searchSeq: 0,
            _resolveTimer: null,
            _resolveSeq: 0,
        }
    },

    computed: {
        emptyMessage() {
            if (!this.searched && !this.activeFamily) {
                return 'Search a code, Pantone number, or color name to see threads.'
            }
            return 'Nothing matched that search.'
        },
    },

    mounted() {
        this.loadFamilies()
        // Show a starting grid immediately rather than an empty picker.
        this.runSearch()
    },

    beforeUnmount() {
        if (this._searchTimer) clearTimeout(this._searchTimer)
        if (this._resolveTimer) clearTimeout(this._resolveTimer)
    },

    methods: {
        async loadFamilies() {
            try {
                const { data } = await api.get('/wilcom/colors/families')
                this.families = data.families || []
            } catch (err) {
                // Non-fatal: search and hex entry still work without the
                // browse buttons. Loud enough for devtools, silent for the
                // user -- losing 11 buttons is not a page-level error.
                // eslint-disable-next-line no-console
                console.warn('thread-color-picker: failed to load families', describeError(err))
            }
        },

        onQueryInput(value) {
            this.query = value
            this.scheduleSearch()
        },

        toggleFamily(name) {
            // A second click on the SAME family clears it -- browsing is a
            // toggle, not a one-way commitment.
            this.activeFamily = this.activeFamily === name ? '' : name
            // A click is a single deliberate action, unlike typing -- debounce
            // exists to collapse a BURST of keystrokes into one request, and
            // applying it here would only add a needless delay after a click.
            // Cancel any pending TYPED search so it can't fire a stale query
            // right after this deliberate one.
            if (this._searchTimer) {
                clearTimeout(this._searchTimer)
                this._searchTimer = null
            }
            this.runSearch()
        },

        scheduleSearch() {
            if (this._searchTimer) clearTimeout(this._searchTimer)
            this._searchTimer = setTimeout(() => {
                this._searchTimer = null
                this.runSearch()
            }, SEARCH_DEBOUNCE_MS)
        },

        async runSearch() {
            const seq = ++this._searchSeq
            this.loading = true
            this.searched = Boolean(this.query.trim())
            try {
                const params = new URLSearchParams()
                if (this.query.trim()) params.set('q', this.query.trim())
                if (this.activeFamily) params.set('family', this.activeFamily)
                params.set('chart', this.chart)
                const { data } = await api.get(
                    `/wilcom/colors/search?${params.toString()}`
                )
                if (seq !== this._searchSeq) return
                this.results = data.threads || []
            } catch (err) {
                if (seq !== this._searchSeq) return
                this.results = []
                // eslint-disable-next-line no-console
                console.warn('thread-color-picker: search failed', describeError(err))
            } finally {
                if (seq === this._searchSeq) this.loading = false
            }
        },

        onHexInput(value) {
            this.hexQuery = value
            this.hexResult = null
            this.hexUnknown = false
            if (!HEX_RE.test(value.trim())) {
                // Same rule as ColorwayEditor.blurStop: a hex that is still
                // being typed is not yet a valid or invalid answer -- only
                // ask the server about a COMPLETE hex.
                if (this._resolveTimer) clearTimeout(this._resolveTimer)
                return
            }
            this.scheduleResolve(value.trim())
        },

        scheduleResolve(value) {
            if (this._resolveTimer) clearTimeout(this._resolveTimer)
            this._resolveTimer = setTimeout(() => {
                this._resolveTimer = null
                this.resolveHex(value)
            }, RESOLVE_DEBOUNCE_MS)
        },

        async resolveHex(value) {
            const seq = ++this._resolveSeq
            try {
                const { data } = await api.post('/wilcom/colors/resolve', {
                    winner: 'hex',
                    hex: value,
                    chart: this.chart,
                })
                if (seq !== this._resolveSeq) return
                if (data.unknown_code || !data.madeira) {
                    this.hexResult = null
                    this.hexUnknown = true
                    return
                }
                this.hexUnknown = false
                this.hexResult = {
                    code: data.madeira,
                    brand: data.brand,
                    hex: data.thread_hex || data.hex,
                    exact: Boolean(data.exact),
                }
            } catch (err) {
                if (seq !== this._resolveSeq) return
                this.hexResult = null
                this.hexUnknown = false
                // eslint-disable-next-line no-console
                console.warn('thread-color-picker: hex resolve failed', describeError(err))
            }
        },

        selectThread(thread) {
            this.$emit('select', thread)
        },
    },
}
</script>

<style scoped>
.tcp {
    display: flex;
    flex-direction: column;
    gap: 12px;
    /* FILL THE HOST (Jake, 2026-09-01: "it only takes about 1/2 of the
       available vertical screen real estate. I want it to extend to the full
       height of the flyover viewport.").

       The drawer was never the problem -- measured on 8089 it was already
       the full 720px of a 720px viewport. The picker inside it was 521px
       tall and its results grid was capped at 320px while holding 4680px of
       content, so the grid ended ~139px short of the drawer floor and the
       scroll was trapped in a short inner box.

       `height: 100%` + `min-height: 0` is what lets the grid below actually
       flex: without min-height a flex child refuses to shrink past its
       content, and the overflow never moves to the grid. */
    height: 100%;
    min-height: 0;
}
.tcp__search {
    position: relative;
    display: flex;
    align-items: center;
}
.tcp__search-icon {
    position: absolute;
    left: 10px;
    color: var(--tf-fg-4, #78909c);
    font-size: 18px;
}
.tcp__search-input {
    width: 100%;
    padding: 8px 10px 8px 34px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    font-size: 0.875rem;
    color: var(--tf-fg-2, #37474f);
    background: var(--tf-bg, #fff);
}
.tcp__families {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0;
    margin: 0;
}
.tcp__family {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 999px;
    padding: 3px 10px 3px 3px;
    background: var(--tf-bg, #fff);
    cursor: pointer;
    font-size: 0.75rem;
    color: var(--tf-fg-2, #37474f);
}
.tcp__family--on {
    border-color: var(--tf-primary, #119ae9);
    box-shadow: 0 0 0 2px rgba(17, 154, 233, 0.18);
}
.tcp__family-swatch {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.15);
}
.tcp__hex {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.tcp__hex-label {
    font-size: 0.75rem;
    color: var(--tf-fg-3, #546e7a);
}
.tcp__hex-input {
    width: 10ch;
    font-family: ui-monospace, Menlo, monospace;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 6px;
    padding: 4px 6px;
    font-size: 0.8125rem;
}
.tcp__hex-result {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    padding: 4px 8px;
    background: var(--tf-bg, #fff);
    cursor: pointer;
    font-size: 0.8125rem;
}
.tcp__hex-unknown {
    font-size: 0.75rem;
    color: var(--tf-warning-dark, #8a5300);
    margin: 0;
}
.tcp__approx-mark {
    color: var(--tf-fg-4, #78909c);
    margin-right: 2px;
}
.tcp__loading,
.tcp__empty {
    font-size: 0.875rem;
    color: var(--tf-fg-3, #546e7a);
    margin: 4px 0;
}
.tcp__grid {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
    padding: 0;
    margin: 0;
    /* GROW INTO WHATEVER IS LEFT, instead of a fixed 320px cap. The cap was
       the actual cause of the half-height flyout: the search box, family
       swatches and hex field take a fixed ~200px, and the grid is the only
       part that should absorb the remainder.

       `min-height: 0` again, for the same reason as on `.tcp` -- a flex
       child with content taller than the box will not shrink without it, and
       the scrollbar would stay on the wrong element. */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}
.tcp__result {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: 1px solid var(--tf-border, #cfd8e3);
    border-radius: 8px;
    padding: 6px 8px;
    background: var(--tf-bg, #fff);
    cursor: pointer;
    text-align: left;
}
.tcp__result:hover {
    border-color: var(--tf-primary, #119ae9);
}
.tcp__swatch {
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 5px;
    border: 1px solid rgba(0, 0, 0, 0.15);
}
.tcp__result-body {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
}
.tcp__result-code {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--tf-fg-1, #133752);
}
.tcp__result-brand,
.tcp__result-family,
.tcp__result-pantone {
    font-size: 0.6875rem;
    color: var(--tf-fg-3, #546e7a);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
